import React, { useContext, useState } from "react";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { API } from "aws-amplify";
import * as CSC from "SC";
import { CommonOutletTemplate } from "UI/CommonOutletTemplate";
import { ClaimNewRealButtonDialog } from "./ClaimNewRealButtonDialog";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { useAppDispatch } from "store/hooks";
import { GlobalContext } from "store/globalContext";
import { showError } from "store/reducers/errorReducer";
import { TextFieldWithEllipsis } from "UI/TextFiledWithEllipsis";
import { EditButtonLocationSuperAdminDialog } from "./EditButtonLocationSuperAdminDialog";
import { useDataGrid } from "hooks/useDataGrid";
import { DataGrid } from "UI/DataGrid";
import { CRITICAL_BATTERY_LEVEL } from "models/constants";
import { useAuthenticator } from "auth/useAuthenticator";

const getColumns = (handleDeleteActivation) => [
  {
    label: "Button ID",
    field: "iotDeviceId",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: ({ iotDeviceId }) => iotDeviceId,
  },
  {
    label: "Name",
    field: "buttonName",
    xs: 1,
    sm: 1,
    md: 1,
    renderer: ({ buttonName }) => buttonName,
  },
  {
    label: "Type",
    field: "manufacturer",
    xs: 1,
    sm: 1,
    md: 1,
    renderer: ({ manufacturer }) => (
      <TextFieldWithEllipsis>{manufacturer}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Practice",
    field: "practiceName",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: ({ practiceName }) => (
      <TextFieldWithEllipsis>{practiceName}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Sender",
    field: "senders",
    xs: 1.5,
    sm: 1.5,
    md: 1.5,
    renderer: ({ senders }) => (
      <TextFieldWithEllipsis>{senders}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Recipient",
    field: "recipientName",
    xs: 1.5,
    sm: 1.5,
    md: 1.5,
    renderer: ({ recipientName }) => (
      <TextFieldWithEllipsis>{recipientName}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Specialists",
    field: "specialists",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: ({ specialists }) => (
      <TextFieldWithEllipsis>{specialists}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Battery",
    field: "lastBatteryLevel",
    align: "center",
    xs: 0.5,
    sm: 0.5,
    md: 0.5,
    renderer: ({ lastBatteryLevel }) => (
      <div
        className={
          lastBatteryLevel <= CRITICAL_BATTERY_LEVEL
            ? "text-red-700"
            : "text-gray-400"
        }
      >
        {lastBatteryLevel ? `${lastBatteryLevel}%` : "N/A"}
      </div>
    ),
  },
  {
    xs: 0.5,
    sm: 0.5,
    md: 0.5,
    align: "center",
    renderer: ({ iotDeviceId, recipientName }) =>
      !recipientName ? (
        <IconButton onClick={() => handleDeleteActivation(iotDeviceId)}>
          <Tooltip title={"Delete physical button activation"}>
            <DeleteForeverIcon />
          </Tooltip>
        </IconButton>
      ) : null,
  },
];

const getMobileColumns = (handleDeleteActivation) => [
  {
    label: "Button ID",
    field: "iotDeviceId",
    xs: 2.75,
    sm: 2.75,
    md: 2.75,
    renderer: ({ iotDeviceId }) => (
      <TextFieldWithEllipsis>{iotDeviceId}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Practice",
    field: "practiceName",
    xs: 4.125,
    sm: 4.125,
    md: 4.125,
    renderer: ({ practiceName }) => (
      <TextFieldWithEllipsis>{practiceName}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Recipient",
    field: "recipientName",
    xs: 4.125,
    sm: 4.125,
    md: 4.125,
    renderer: ({ recipientName }) => (
      <TextFieldWithEllipsis>{recipientName}</TextFieldWithEllipsis>
    ),
  },
  {
    xs: 1,
    sm: 1,
    md: 1,
    renderer: ({ iotDeviceId, recipientName }) =>
      !recipientName ? (
        <IconButton onClick={() => handleDeleteActivation(iotDeviceId)}>
          <Tooltip title={"Delete physical button activation"}>
            <DeleteForeverIcon />
          </Tooltip>
        </IconButton>
      ) : null,
  },
];

export const HardButtonActivations = () => {
  const { handleAuthError } = useAuthenticator();
  const dispatch = useAppDispatch();
  const { userData, showWarning, openConfirmationWithCallback } =
    useContext(GlobalContext);
  const [isAddNewDialogOpen, setIsAddNewDialogOpen] = useState(false);
  const [currentButtonId, setCurrentButtonId] = useState("");

  const dataGrid = useDataGrid({
    url: "/buttons",
    defaultSorter: {
      name: "iotDeviceId",
      order: "asc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-IoT-Buttons`,
  });

  const handleDeleteActivation = (deviceId) =>
    openConfirmationWithCallback({
      title: "Please confirm",
      message: (
        <>
          Button activation <strong>{deviceId}</strong> will be deleted.
          Proceed?
        </>
      ),
      callbackFn: async () => {
        try {
          const result = await API.del("restApi", `/buttons`, {
            body: {
              deviceId,
            },
          });

          if (result?.error) {
            console.error(result.error);
            dispatch(showError(result.error));
            return;
          }

          showWarning({
            title: (
              <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  Success
                </Typography>
              </Stack>
            ),
            content: <>Button activation deleted successfully</>,
          });
          dataGrid?.loadData();
        } catch (error) {
          handleAuthError(error);
        }
      },
      buttonLabels: ["No", "Yes"],
    });

  const handleRowClick = (row) => {
    if (!row?.recipientName) return;

    setCurrentButtonId(row.iotDeviceId);
  };

  return (
    <CommonOutletTemplate title="Physical Button Activation">
      <DataGrid
        columns={getColumns(handleDeleteActivation)}
        mobileColumns={getMobileColumns(handleDeleteActivation)}
        handleRowClick={handleRowClick}
        {...dataGrid}
        afterSearchContent={
          <CSC.BlueButton
            variant="outlined"
            onClick={() => setIsAddNewDialogOpen(true)}
          >
            Add Button Device
          </CSC.BlueButton>
        }
      />
      <ClaimNewRealButtonDialog
        open={isAddNewDialogOpen}
        handleClose={() => setIsAddNewDialogOpen(false)}
        refreshList={() => dataGrid?.loadData()}
      />
      {currentButtonId && (
        <EditButtonLocationSuperAdminDialog
          buttonId={currentButtonId}
          handleClose={() => setCurrentButtonId("")}
          refreshList={() => dataGrid?.loadData()}
        />
      )}
    </CommonOutletTemplate>
  );
};
